(function() {
  'use strict';

  const WRAPPER_SELECTOR = '[data-al-pdp-variant-visibility]';
  const PRODUCT_SELECTOR = '[data-section-type="product"], [data-quick-add-product]';
  const PRODUCT_SCOPE_ATTRIBUTE = 'data-al-pdp-variant-visibility-scope';
  const VARIANT_JSON_SELECTOR = '[data-variant-json]';
  const INPUT_SELECTOR = '[dynamic-variant-input]';
  const OPTION_GROUP_SELECTOR = '[dynamic-variant-input-wrap]';
  const BUTTON_SELECTOR = '[dynamic-variant-button]';
  const MESSAGE_SELECTOR = '[data-variant-availability-message]';
  const HIDDEN_CLASS = 'al-variant-hidden';
  const SOLD_OUT_CLASS = 'al-variant-sold-out';
  const UNAVAILABLE_CLASS = 'al-variant-unavailable';
  const THEME_DISABLED_CLASS = 'disabled';
  const OPTION_INDEXES = ['option1', 'option2', 'option3'];

  const TEMPORARILY_OUT_OF_STOCK_MESSAGE =
    'Temporarily out of stock. <a href="/pages/contact-us" target="_blank" rel="noopener noreferrer">Contact our support team</a> for an ETA or to preorder.';
  const UNAVAILABLE_MESSAGE = 'This option combination is unavailable.';

  function normalizeValue(value) {
    return String(value == null ? '' : value);
  }

  function parseVariants(product) {
    const json = product && product.querySelector(VARIANT_JSON_SELECTOR);

    if (!json) {
      return null;
    }

    try {
      return JSON.parse(json.textContent);
    } catch (error) {
      return null;
    }
  }

  function getOptionInputs(wrapper) {
    return Array.from(wrapper.querySelectorAll(INPUT_SELECTOR));
  }

  function getSelectedOptions(wrapper) {
    return getOptionInputs(wrapper).reduce(function(options, input) {
      const index = input.dataset.index || input.id;

      if (index) {
        options[index] = input.value;
      }

      return options;
    }, {});
  }

  function findVariant(variants, selectedOptions) {
    return variants.find(function(variant) {
      return Object.keys(selectedOptions).every(function(index) {
        return normalizeValue(variant[index]) === normalizeValue(selectedOptions[index]);
      });
    }) || null;
  }

  function getOptionIndexes(variants) {
    return OPTION_INDEXES.filter(function(optionIndex) {
      return variants.some(function(variant) {
        return variant[optionIndex] != null;
      });
    });
  }

  function getAvailabilityKey(optionIndexes, options) {
    return JSON.stringify(optionIndexes.map(function(optionIndex) {
      return normalizeValue(options[optionIndex]);
    }));
  }

  function buildAvailabilityIndex(variants, optionIndexes) {
    const availabilityIndex = new Map();

    variants.forEach(function(variant) {
      const key = getAvailabilityKey(optionIndexes, variant);
      const availability = availabilityIndex.get(key) || {
        exists: false,
        available: false
      };

      availability.exists = true;
      availability.available = availability.available || Boolean(variant.available);
      availabilityIndex.set(key, availability);
    });

    return availabilityIndex;
  }

  function getCandidateState(availabilityIndex, optionIndexes, selectedOptions, optionIndex, value) {
    const candidateOptions = Object.assign({}, selectedOptions);
    candidateOptions[optionIndex] = value;

    const hasCompleteSelection = optionIndexes.every(function(index) {
      return candidateOptions[index] != null;
    });

    // Avoid hiding options while the theme is still initializing its inputs.
    if (!hasCompleteSelection) {
      return 'available';
    }

    const availability = availabilityIndex.get(
      getAvailabilityKey(optionIndexes, candidateOptions)
    );

    if (!availability || !availability.exists) {
      return 'unavailable';
    }

    return availability.available ? 'available' : 'sold-out';
  }

  function buildGloballyAvailableOptions(variants) {
    return variants.reduce(function(availableOptions, variant) {
      if (!variant.available) {
        return availableOptions;
      }

      ['option1', 'option2', 'option3'].forEach(function(index) {
        if (variant[index] == null) {
          return;
        }

        if (!availableOptions[index]) {
          availableOptions[index] = new Set();
        }

        availableOptions[index].add(normalizeValue(variant[index]));
      });

      return availableOptions;
    }, {});
  }

  function isGloballyAvailable(availableOptions, optionIndex, value) {
    return Boolean(
      availableOptions[optionIndex] &&
      availableOptions[optionIndex].has(normalizeValue(value))
    );
  }

  function shouldHideOption(state, globallyAvailable, selected, settings) {
    if (selected) {
      return false;
    }

    if (!globallyAvailable) {
      return true;
    }

    if (state === 'sold-out') {
      return !settings.showOutOfStock;
    }

    if (state === 'unavailable') {
      return !settings.showUnavailable;
    }

    return false;
  }

  function updateButton(button, state, globallyAvailable, selected, settings) {
    const hidden = shouldHideOption(state, globallyAvailable, selected, settings);
    const unavailable = state === 'unavailable';
    const soldOut = state === 'sold-out';

    const disabled = soldOut || unavailable || !globallyAvailable;

    if (button.hidden !== hidden) {
      button.hidden = hidden;
    }

    button.classList.toggle(HIDDEN_CLASS, hidden);
    button.classList.toggle(SOLD_OUT_CLASS, soldOut);
    button.classList.toggle(UNAVAILABLE_CLASS, unavailable);
    button.classList.toggle(THEME_DISABLED_CLASS, disabled);

    // These states remain selectable so customers can see the availability message.
    button.removeAttribute('aria-disabled');
  }

  function updateSelectOption(option, state, globallyAvailable, settings) {
    const hidden = shouldHideOption(state, globallyAvailable, option.selected, settings);

    if (option.hidden !== hidden) {
      option.hidden = hidden;
    }

    if (option.disabled !== hidden) {
      option.disabled = hidden;
    }
  }

  function updateOptionStates(state) {
    const selectedOptions = getSelectedOptions(state.controlsRoot);
    const groups = Array.from(state.controlsRoot.querySelectorAll(OPTION_GROUP_SELECTOR));

    groups.forEach(function(group) {
      const optionIndex = group.dataset.index;

      if (!optionIndex) {
        return;
      }

      Array.from(group.querySelectorAll(BUTTON_SELECTOR)).forEach(function(button) {
        const value = button.dataset.optionValue;

        if (value == null) {
          return;
        }

        const selected = normalizeValue(selectedOptions[optionIndex]) === normalizeValue(value);
        const globallyAvailable = isGloballyAvailable(state.availableOptions, optionIndex, value);
        const candidateState = getCandidateState(
          state.availabilityIndex,
          state.optionIndexes,
          selectedOptions,
          optionIndex,
          value
        );

        updateButton(button, candidateState, globallyAvailable, selected, state.settings);
      });

      const select = group.querySelector('select' + INPUT_SELECTOR);

      if (select) {
        Array.from(select.options).forEach(function(option) {
          const globallyAvailable = isGloballyAvailable(state.availableOptions, optionIndex, option.value);
          const candidateState = getCandidateState(
            state.availabilityIndex,
            state.optionIndexes,
            selectedOptions,
            optionIndex,
            option.value
          );

          updateSelectOption(option, candidateState, globallyAvailable, state.settings);
        });
      }
    });
  }

  function updateMessage(state) {
    const message = state.controlsRoot.querySelector(MESSAGE_SELECTOR);

    if (!message) {
      return;
    }

    const selectedVariant = findVariant(state.variants, getSelectedOptions(state.controlsRoot));

    if (!selectedVariant) {
      if (message.textContent !== UNAVAILABLE_MESSAGE) {
        message.textContent = UNAVAILABLE_MESSAGE;
      }
      if (message.hidden) {
        message.hidden = false;
      }
      return;
    }

    if (!selectedVariant.available) {
      if (message.innerHTML !== TEMPORARILY_OUT_OF_STOCK_MESSAGE) {
        message.innerHTML = TEMPORARILY_OUT_OF_STOCK_MESSAGE;
      }
      if (message.hidden) {
        message.hidden = false;
      }
      return;
    }

    if (!message.hidden) {
      message.hidden = true;
    }
    if (message.textContent) {
      message.textContent = '';
    }
  }

  function update(state) {
    updateOptionStates(state);
    updateMessage(state);
  }

  function scheduleUpdate(state) {
    if (state.updateFrame != null) {
      return;
    }

    state.updateFrame = window.requestAnimationFrame(function() {
      state.updateFrame = null;
      update(state);
    });
  }

  function initWrapper(wrapper) {
    if (wrapper.dataset.alVariantVisibilityInitialized === 'true') {
      return;
    }

    const product = wrapper.closest(PRODUCT_SELECTOR);
    const variants = parseVariants(product);

    if (!variants) {
      return;
    }

    const optionIndexes = getOptionIndexes(variants);
    const state = {
      wrapper: wrapper,
      controlsRoot: product,
      variants: variants,
      optionIndexes: optionIndexes,
      availableOptions: buildGloballyAvailableOptions(variants),
      availabilityIndex: buildAvailabilityIndex(variants, optionIndexes),
      updateFrame: null,
      settings: {
        showOutOfStock: wrapper.dataset.showOutOfStockOptions !== 'false',
        showUnavailable: wrapper.dataset.showUnavailableCombinations === 'true'
      }
    };

    product.setAttribute(PRODUCT_SCOPE_ATTRIBUTE, '');
    wrapper.dataset.alVariantVisibilityInitialized = 'true';
    wrapper.alVariantVisibilityState = state;

    scheduleUpdate(state);
  }

  function init(root) {
    const scope = root || document;
    const wrappers = scope.matches && scope.matches(WRAPPER_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(WRAPPER_SELECTOR));

    wrappers.forEach(initWrapper);
  }

  function updateInitializedWrappers(root) {
    const scope = root || document;

    Array.from(scope.querySelectorAll(WRAPPER_SELECTOR)).forEach(function(wrapper) {
      if (wrapper.alVariantVisibilityState) {
        scheduleUpdate(wrapper.alVariantVisibilityState);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('product:variant-change', function() {
    updateInitializedWrappers(document);
  });

  document.addEventListener('quickview:loaded', function() {
    init(document);
  });

  document.addEventListener('shopify:section:load', function(event) {
    init(event.target);
  });
})();
